import { Box, Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'

interface KeyboardShortcutsDialogProps {
  enabled: boolean
  direction: 'rtl' | 'ltr'
}

interface ShortcutRow {
  /** One chip per key, shown LTR in press order. `keysHe` overrides when a label needs Hebrew (clicks, drag). */
  keys: string[]
  keysHe?: string[]
  he: string
  en: string
}

interface ShortcutSection {
  he: string
  en: string
  rows: ShortcutRow[]
}

const SECTIONS: ShortcutSection[] = [
  {
    he: 'טאבים וניווט',
    en: 'Tabs & navigation',
    rows: [
      { keys: ['⌥', 'Tab'], he: 'מעבר בין טאבים — החזק ⌥, שחרר למעבר', en: 'Switch tabs — hold ⌥, release to switch' },
      { keys: ['⌥', '⇧', 'Tab'], he: 'מעבר בין טאבים אחורה', en: 'Switch tabs backwards' },
      { keys: ['⌥', 'W'], he: 'סגירת הטאב הנוכחי', en: 'Close the current tab' },
      {
        keys: ['⌘', 'Click'],
        keysHe: ['⌘', 'קליק'],
        he: 'פתיחת תוצאת חיפוש בטאב חדש',
        en: 'Open search result in a new tab',
      },
      { keys: ['Middle-click'], keysHe: ['קליק אמצעי'], he: 'סגירת טאב', en: 'Close a tab' },
      { keys: ['Drag'], keysHe: ['גרירה'], he: 'סידור מחדש של הטאבים', en: 'Reorder tabs' },
      { keys: ['⌘', '/'], he: 'קפיצה לחיפוש', en: 'Focus search' },
      { keys: ['⌥', '/'], he: 'הצגת חלון זה (בפריסה אנגלית)', en: 'Show this dialog' },
    ],
  },
  {
    he: 'עריכה',
    en: 'Editing',
    rows: [
      { keys: ['⌘', 'S'], he: 'שמירה', en: 'Save' },
      { keys: ['⌘', 'Z'], he: 'ביטול', en: 'Undo' },
      { keys: ['⇧', '⌘', 'Z'], he: 'ביצוע מחדש', en: 'Redo' },
      { keys: ['⌘', 'K'], he: 'הוספת קישור', en: 'Insert link' },
      { keys: ['⌘', '⌥', 'E'], he: 'סימון טקסט להצפנה', en: 'Mark selection as encrypted' },
      { keys: ['⌘', '⌥', 'T'], he: 'תיוג הטקסט המסומן', en: 'Tag selection' },
    ],
  },
  {
    he: 'עיצוב טקסט',
    en: 'Formatting',
    rows: [
      { keys: ['⌘', 'B'], he: 'מודגש', en: 'Bold' },
      { keys: ['⌘', 'I'], he: 'נטוי', en: 'Italic' },
      { keys: ['⌘', 'U'], he: 'קו תחתון', en: 'Underline' },
      { keys: ['⌘', '⌥', '0'], he: 'טקסט רגיל', en: 'Normal text' },
      { keys: ['⌘', '⌥', '1–5'], he: 'כותרת / תת־כותרת / כותרות 1–3', en: 'Title / subtitle / headings 1–3' },
      { keys: ['⇧', '⌘', '7'], he: 'רשימה ממוספרת', en: 'Numbered list' },
      { keys: ['⇧', '⌘', '8'], he: 'רשימת תבליטים', en: 'Bulleted list' },
    ],
  },
]

const KeyChip: React.FC<{ label: string }> = ({ label }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 26,
      height: 26,
      px: 0.9,
      borderRadius: 1,
      border: '1px solid var(--toolbar-page-border)',
      borderBottomWidth: 2,
      bgcolor: 'var(--bg-hover)',
      fontSize: 15,
      lineHeight: 1,
      fontWeight: 600,
      fontFamily: 'inherit',
      color: 'var(--text-primary)',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
)

/**
 * Reference dialog listing the app's keyboard shortcuts, opened with ⌥/.
 * Self-contained (own listener + state) so it never re-renders BookEditor.
 */
export default function KeyboardShortcutsDialog({ enabled, direction }: KeyboardShortcutsDialogProps) {
  const [open, setOpen] = useState(false)
  const isHebrew = direction === 'rtl'

  useEffect(() => {
    if (!enabled) {
      return
    }
    const onKeyDown = (e: KeyboardEvent) => {
      // ⌥ + physical Slash key. Known limitation: macOS swallows this combo when
      // the Hebrew layout is active — the dialog opens from an English layout only.
      if (e.altKey && !e.metaKey && !e.ctrlKey && e.code === 'Slash') {
        e.preventDefault()
        e.stopPropagation()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [enabled])

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth dir={direction}>
      <DialogTitle sx={{ fontSize: 17, fontWeight: 600, pb: 1 }}>
        {isHebrew ? 'קיצורי מקלדת' : 'Keyboard shortcuts'}
      </DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        {SECTIONS.map((section) => (
          <Box key={section.en} sx={{ '& + &': { mt: 2.25 } }}>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: 'var(--text-secondary)',
                mb: 0.75,
              }}
            >
              {isHebrew ? section.he : section.en}
            </Typography>
            {section.rows.map((row) => {
              const chips = isHebrew && row.keysHe ? row.keysHe : row.keys
              return (
                <Box
                  key={row.keys.join('+')}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    py: 0.55,
                  }}
                >
                  <Typography sx={{ fontSize: 13.5, color: 'var(--text-primary)' }}>
                    {isHebrew ? row.he : row.en}
                  </Typography>
                  <Box dir="ltr" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    {chips.map((key) => (
                      <KeyChip key={key} label={key} />
                    ))}
                  </Box>
                </Box>
              )
            })}
          </Box>
        ))}
        <Typography
          dir="ltr"
          sx={{
            mt: 2.5,
            pt: 1.25,
            borderTop: '1px solid var(--toolbar-page-border)',
            fontSize: 12,
            color: 'var(--text-secondary)',
            textAlign: 'center',
          }}
        >
          ⌘ Command&nbsp;&nbsp;·&nbsp;&nbsp;⌥ Option&nbsp;&nbsp;·&nbsp;&nbsp;⇧ Shift
        </Typography>
      </DialogContent>
    </Dialog>
  )
}
